import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  ArrowLeft,
  Calendar,
  Clock,
  Activity,
  Zap,
  CheckCircle2,
  XCircle,
  Trophy,
  Rocket,
  ArrowRight,
  BookOpen,
  Quote,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

const Stat = ({ icon: Icon, value, label }) => (
  <div className="flex items-start gap-4">
    <div className="w-11 h-11 rounded-full flex items-center justify-center bg-primary/10 flex-shrink-0">
      <Icon className="w-5 h-5 text-primary" />
    </div>
    <div>
      <p className="font-heading text-2xl md:text-3xl font-bold text-foreground leading-tight">
        {value}
      </p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  </div>
);

const Science = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // SEO: override default meta for this high-value content page
    const prevTitle = document.title;
    document.title =
      "The Science: How 6 Weeks Rewires Your Memory | MindForge";

    const setMeta = (selector, attr, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace(/^meta\[/, "").replace(/\]$/, "").split("=");
        el.setAttribute(k, v.replace(/"/g, ""));
        document.head.appendChild(el);
      }
      const before = el.getAttribute(attr);
      el.setAttribute(attr, content);
      return () => before === null ? el.removeAttribute(attr) : el.setAttribute(attr, before);
    };

    const desc =
      "In 2017, scientists scanned the brains of the world's top memory champions. They expected to find something special — they didn't. Then they trained 51 ordinary people for six weeks and the brains changed. The science behind MindForge.";
    const restoreDesc = setMeta('meta[name="description"]', "content", desc);
    const restoreOgTitle = setMeta(
      'meta[property="og:title"]',
      "content",
      "How 6 weeks rewires your memory — the science behind MindForge"
    );
    const restoreOgDesc = setMeta(
      'meta[property="og:description"]',
      "content",
      desc
    );
    const restoreOgUrl = setMeta(
      'meta[property="og:url"]',
      "content",
      "https://mindforge.memoryschool.com/science"
    );

    // Canonical link for this page
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevHref = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://mindforge.memoryschool.com/science");

    (async () => {
      try {
        const res = await fetch(`${API}/testimonials`);
        if (res.ok) {
          const data = await res.json();
          setTestimonials(data.testimonials || []);
        }
      } catch (e) {
        // silent — testimonials are optional
      }
    })();

    return () => {
      document.title = prevTitle;
      restoreDesc();
      restoreOgTitle();
      restoreOgDesc();
      restoreOgUrl();
      if (prevHref) canonical.setAttribute("href", prevHref);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="science-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border/40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="science-back-btn"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="font-heading font-bold tracking-tight text-lg">
            <span className="text-primary">MIND</span>FORGE
          </div>
          <div className="w-12"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        {/* Eyebrow */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary text-xs uppercase tracking-widest font-semibold rounded-full">
            <BookOpen className="w-3.5 h-3.5" />
            The Science Behind It
          </span>
        </motion.div>

        {/* Hero */}
        <motion.h1
          initial="hidden"
          animate="show"
          variants={fadeUp}
          transition={{ delay: 0.05 }}
          className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight"
        >
          How 6 weeks <span className="text-primary">rewires</span> your memory
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="show"
          variants={fadeUp}
          transition={{ delay: 0.1 }}
          className="text-xl text-muted-foreground leading-relaxed mb-12"
        >
          A short, evidence-based read on why memory is a trainable skill — and how MindForge gives you the same daily reps neuroscientists used to rewire 51 ordinary brains.
        </motion.p>

        {/* The study */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">
            The study that changed everything
          </p>
          <p className="text-lg leading-relaxed text-foreground/90 mb-5">
            In 2017, neuroscientist Martin Dresler and his team at Radboud University did
            something nobody had done at this scale before.
          </p>
          <p className="text-lg leading-relaxed text-foreground/90 mb-5">
            They put <strong>23 of the world's top memory athletes</strong> — people who can
            memorise the order of a shuffled deck of cards in under 30 seconds — into an{" "}
            <strong>fMRI brain scanner</strong>.
          </p>
          <p className="text-lg leading-relaxed text-foreground/90">
            Then they did the same with 23 ordinary people, carefully matched for age, sex
            and IQ. Some controls were even pulled from Mensa, just to make sure they
            weren't comparing geniuses to average folks.
          </p>
        </motion.section>

        {/* What they found */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">
            What they actually found
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-5 leading-tight">
            The champions' brains were{" "}
            <span className="text-primary">structurally identical</span> to everyone else's.
          </h2>
          <Card className="bg-card border-border mt-8">
            <CardContent className="p-6 md:p-8 space-y-4">
              <div className="flex items-center gap-3 text-foreground/90">
                <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <span>No bigger hippocampus</span>
              </div>
              <div className="flex items-center gap-3 text-foreground/90">
                <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <span>No denser grey matter</span>
              </div>
              <div className="flex items-center gap-3 text-foreground/90">
                <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <span>No magic memory region</span>
              </div>
            </CardContent>
          </Card>
          <p className="text-lg leading-relaxed text-foreground/90 mt-8">
            The only difference was in <strong>how their brain networks connected</strong>{" "}
            during memory tasks. The wiring was different. The hardware was the same.
          </p>
        </motion.section>

        {/* The experiment */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">
            Then they ran the experiment that matters
          </p>
          <p className="text-lg leading-relaxed text-foreground/90 mb-8">
            The team took 51 ordinary people with no memory training and gave them a simple
            protocol:
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <Stat icon={Clock} value="20–30 min" label="per day" />
            <Stat icon={Calendar} value="6 weeks" label="of practice" />
            <Stat icon={Brain} value="Method of loci" label="memory palaces" />
          </div>

          <Card className="bg-primary/5 border-primary/30">
            <CardContent className="p-6 md:p-8">
              <p className="text-lg leading-relaxed text-foreground">
                After six weeks, they scanned the trainees' brains again.
              </p>
              <p className="font-heading text-xl md:text-2xl text-primary mt-4 leading-tight font-semibold">
                The connectivity patterns had shifted toward those of the world champions.
              </p>
              <p className="text-foreground/90 mt-4">
                And the more their brains shifted, the better their memory got.
              </p>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Activity className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading text-2xl font-bold mb-1">26–30 → 62</p>
                <p className="text-sm text-muted-foreground">
                  Words recalled out of 72 — more than doubled
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Calendar className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading text-2xl font-bold mb-1">4 months later</p>
                <p className="text-sm text-muted-foreground">
                  The gains were still there
                </p>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* And it gets better */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">
            And it gets better
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-5 leading-tight">
            After training, your brain uses{" "}
            <span className="text-primary">less energy</span> to memorise the same amount of
            information.
          </h2>
          <p className="text-lg leading-relaxed text-foreground/90 mb-8">
            A 2021 follow-up from the same lab (Wagner &amp; Dresler) found something even
            more interesting. Your brain doesn't just get better — it gets more efficient.
          </p>
          <div className="flex items-center gap-3 p-5 bg-primary/5 border border-primary/30 rounded-md">
            <Zap className="w-6 h-6 text-primary flex-shrink-0" />
            <p className="text-lg font-semibold text-foreground">
              Same task. Less effort.
            </p>
          </div>
        </motion.section>

        {/* What this means for you */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">
            What this actually means for you
          </p>
          <p className="text-lg leading-relaxed text-foreground/90 mb-5 italic">
            "For 30 years, when people have asked me how long this stuff takes to click,
            I've said the same thing: about six weeks. Twenty minutes a day. Then Dresler
            and his team put 23 of the world's best into an fMRI scanner and confirmed the
            number."
          </p>
          <p className="text-lg leading-relaxed text-foreground/90 mb-8">
            So the truth about a great memory is this:
          </p>

          <div className="space-y-3 mb-8">
            <div className="flex items-center gap-3 p-3 bg-card border border-border rounded">
              <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground/90">It's not a gift you're born with</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-card border border-border rounded">
              <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground/90">It's not a personality trait</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-card border border-border rounded">
              <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <span className="text-foreground/90">It's not about being "good with names"</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/30 rounded">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-foreground font-medium">It's a skill</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/30 rounded">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-foreground font-medium">Anyone can learn it</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/30 rounded">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-foreground font-medium">
                Six weeks of focused practice rewires the network
              </span>
            </div>
          </div>

          <Card className="bg-card border-border">
            <CardContent className="p-6 md:p-8 space-y-3">
              <p className="text-foreground/90">
                <strong className="text-foreground">The bad news:</strong> it's a skill, so it takes effort.
              </p>
              <p className="text-foreground/90">
                <strong className="text-primary">The good news:</strong> it's a skill, so anyone can learn it and get good at it.
              </p>
            </CardContent>
          </Card>
        </motion.section>

        {/* Testimonials — From the trainees */}
        {testimonials.length > 0 && (
          <motion.section
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-16"
            data-testid="science-testimonials-section"
          >
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">
              From the trainees
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8 leading-tight">
              People reproducing the result
            </h2>
            <div className="space-y-4">
              {testimonials.map((t, i) => (
                <Card
                  key={t.testimonial_id}
                  className="bg-card border-border"
                  data-testid={`testimonial-${t.testimonial_id}`}
                >
                  <CardContent className="p-6 md:p-8">
                    <Quote
                      className={`w-6 h-6 mb-4 ${
                        i % 2 === 0 ? "text-primary" : "text-secondary"
                      }`}
                    />
                    <p className="text-lg leading-relaxed text-foreground/90 mb-4 whitespace-pre-line">
                      {t.quote}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <span className="text-foreground font-medium">
                        {t.author_name}
                      </span>
                      {t.author_role ? ` · ${t.author_role}` : ""}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.section>
        )}

        {/* Where MindForge comes in */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-primary mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            Where MindForge comes in
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 leading-tight">
            The same daily reps the science validated — built into a format your modern
            brain will actually stick with.
          </h2>

          <div className="space-y-3 mb-8">
            <div className="flex items-start gap-3 p-4 bg-card border border-border rounded">
              <span className="text-2xl flex-shrink-0">🧱</span>
              <div>
                <p className="font-medium text-foreground">Short sessions</p>
                <p className="text-sm text-muted-foreground">5–20 minutes, anywhere</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-card border border-border rounded">
              <span className="text-2xl flex-shrink-0">🔁</span>
              <div>
                <p className="font-medium text-foreground">Spaced practice</p>
                <p className="text-sm text-muted-foreground">
                  Streaks &amp; weekly goals keep the schedule consistent — without nagging
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-card border border-border rounded">
              <span className="text-2xl flex-shrink-0">🏛️</span>
              <div>
                <p className="font-medium text-foreground">
                  The core techniques memory champions actually use
                </p>
                <p className="text-sm text-muted-foreground">
                  Numbers, words, and names — broken into bite-sized rounds
                </p>
              </div>
            </div>
          </div>

          <p className="text-lg leading-relaxed text-foreground/90 mb-8">
            <strong className="text-foreground">Six weeks. Twenty minutes a day.</strong>{" "}
            The same protocol the science validated. The work happens quietly in the
            background while your brain rewires itself.
          </p>

          <p className="text-lg leading-relaxed text-foreground/90 italic mb-8">
            The champions in that fMRI scanner started exactly where you are right now.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate("/?startTrial=1")}
              data-testid="science-cta-trial-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg font-semibold"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Start your 7-day free trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="px-8 py-6 text-lg font-semibold"
            >
              Back to MindForge
            </Button>
          </div>
        </motion.section>

        {/* Reference */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          variants={fadeUp}
          className="mt-20 pt-10 border-t border-border"
        >
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
            References
          </p>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              Dresler, M., Shirer, W. R., Konrad, B. N., Müller, N. C. J., Wagner, I. C.,
              Fernández, G., Czisch, M., &amp; Greicius, M. D. (2017).{" "}
              <em className="text-foreground/80">
                Mnemonic Training Reshapes Brain Networks to Support Superior Memory.
              </em>{" "}
              Neuron, 93(5), 1227–1235.
            </p>
            <p>
              Wagner, I. C., Konrad, B. N., Schuster, P., Weisig, S., Repantis, D., Ohla,
              K., Kühn, S., Fernández, G., Steiger, A., Lamm, C., Czisch, M., &amp;
              Dresler, M. (2021).{" "}
              <em className="text-foreground/80">
                Durable Memories and Efficient Neural Coding Through Mnemonic Training
                Using the Method of Loci.
              </em>{" "}
              Science Advances, 7(10).
            </p>
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default Science;
